import { useQuery } from '@tanstack/react-query';

import { useState } from 'react';

import { Add, Delete } from '@mui/icons-material';
import { Autocomplete, Button, IconButton, MenuItem, Stack, TextField, Typography } from '@mui/material';

import { useApiClient } from '@@common';

import { ContextClient, UserInterfaceClient } from '../../api/api';
import { AuthoringResourceScopeType, AuthoringResourceTarget } from '../../api/api';
import { Permissions } from '../../common/Permissions';
import useDebouncedCallback from '../../common/hooks/useDebouncedCallback';
import useHasPermission from '../../common/hooks/useHasPermission';

export default function ResourceTargetsEditor({
	value,
	onChange,
}: {
	value: AuthoringResourceTarget[];
	onChange: (targets: AuthoringResourceTarget[]) => void;
}) {
	const contexts = useApiClient(ContextClient);
	const apps = useApiClient(UserInterfaceClient);
	const [appSearch, setAppSearch] = useState('');
	const searchApps = useDebouncedCallback(setAppSearch, 300);
	const canReadServices = useHasPermission(Permissions.ServiceRead);
	const canReadApps = useHasPermission(Permissions.AppRead);
	const { data: domains = [] } = useQuery({ queryKey: ['resource-target-domains'], queryFn: () => contexts.getSubDomains(), enabled: canReadServices });
	const { data: appResult } = useQuery({
		queryKey: ['resource-target-apps', appSearch],
		queryFn: () => apps.search(undefined, undefined, appSearch || undefined, 0, 100),
		enabled: canReadApps,
	});
	const optionsFor = (type: AuthoringResourceScopeType) =>
		type === AuthoringResourceScopeType.App
			? (appResult?.items ?? []).map((app) => ({ id: app.definitionId!, name: app.name ?? app.definitionId! }))
			: type === AuthoringResourceScopeType.SubDomain
				? domains.map((domain) => ({ id: domain.subDomain!.id!, name: domain.subDomain!.name ?? domain.subDomain!.id! }))
				: domains.flatMap((domain) => (domain.contexts ?? []).map((context) => ({ id: context.id!, name: context.contextName ?? context.id! })));
	const update = (index: number, target: AuthoringResourceTarget) => onChange(value.map((existing, i) => (i === index ? target : existing)));
	return (
		<Stack spacing={1}>
			<Typography variant="subtitle2">Applies to</Typography>
			{value.map((target, index) => {
				const options = optionsFor(target.type);
				const selected = target.id ? (options.find((option) => option.id === target.id) ?? { id: target.id, name: target.id }) : null;
				return (
					<Stack key={index} direction="row" spacing={1} alignItems="center">
						<TextField
							select
							label="Scope"
							value={target.type}
							onChange={(event) => update(index, { type: event.target.value as AuthoringResourceScopeType })}
							sx={{ minWidth: 160 }}
						>
							{Object.values(AuthoringResourceScopeType).map((type) => (
								<MenuItem key={type} value={type}>
									{type === AuthoringResourceScopeType.Tenant ? 'Entire tenant' : type}
								</MenuItem>
							))}
						</TextField>
						{target.type !== AuthoringResourceScopeType.Tenant && (
							<Autocomplete
								fullWidth
								options={options}
								value={selected}
								filterOptions={target.type === AuthoringResourceScopeType.App ? (options) => options : undefined}
								onInputChange={(_, text, reason) => {
									if (target.type === AuthoringResourceScopeType.App && reason === 'input') {
										searchApps(text);
									}
								}}
								getOptionLabel={(option) => option.name}
								isOptionEqualToValue={(a, b) => a.id === b.id}
								onChange={(_, selected) => update(index, { type: target.type, id: selected?.id })}
								renderInput={(params) => <TextField {...params} label={`Select ${target.type}`} />}
							/>
						)}
						<IconButton aria-label={`Remove target ${index + 1}`} disabled={value.length === 1} onClick={() => onChange(value.filter((_, i) => i !== index))}>
							<Delete />
						</IconButton>
					</Stack>
				);
			})}
			<Button startIcon={<Add />} sx={{ alignSelf: 'flex-start' }} onClick={() => onChange([...value, { type: AuthoringResourceScopeType.App }])}>
				Add target
			</Button>
			<Typography variant="caption" color="text.secondary">
				One document can apply to several targets. Tenant resources are inherited; context resources also inherit their subdomain resources.
			</Typography>
		</Stack>
	);
}
